// ---------------------------------------------------------------------------
// CFRI demo portal (/cfri). One shared password, no account.
//
// The password is checked by the backend (POST /cfri/unlock), which answers
// with a time-limited pass. Only that pass is kept in localStorage, never the
// password, and the report itself is fetched from the backend with it. None of
// the (confidential) content ships in this bundle, which is public static
// files: a password checked here in the browser would protect nothing.
//
// Deliberately plain fetch(), not HttpService.request(): these calls must not
// carry (or try to refresh) a DeepThought login, because portal visitors have
// none, and request()'s 401 handling assumes one.
// ---------------------------------------------------------------------------
import { properties } from "../properties";
import { ftCfriPass } from "../constants/Constants";

const PASS_HEADER = "X-Cfri-Token";

// The report is fetched once per page load and shared by every portal page;
// contentCache lets a page that mounts after that render it immediately.
let contentPromise = null;
let contentCache = null;

export class CfriLockedError extends Error {}

function readPass() {
    try {
        const pass = JSON.parse(localStorage.getItem(ftCfriPass));
        if (pass?.token && pass.expiresAt > Date.now()) return pass;
    } catch { /* missing or malformed: treat as locked */ }
    return null;
}

export function hasPass() {
    return readPass() !== null;
}

export function lock() {
    localStorage.removeItem(ftCfriPass);
    contentPromise = null;
    contentCache = null;
}

/**
 * Exchanges the password for a pass. Resolves on success; rejects with an
 * Error whose message is fit to show under the password field.
 */
export async function unlock(password) {
    let response;
    try {
        response = await fetch(`${properties.host}/cfri/unlock`, {
            method: "POST",
            headers: { "Content-Type": "application/json;charset=UTF-8" },
            body: JSON.stringify({ password }),
        });
    } catch {
        throw new Error("Couldn't reach the server. Check your connection and try again.");
    }
    const body = await response.json().catch(() => ({}));
    if (!response.ok) {
        throw new Error(
            response.status === 429 && !body.message
                ? "Too many attempts. Please wait a minute and try again."
                : body.message || "Something went wrong. Please try again."
        );
    }
    localStorage.setItem(ftCfriPass, JSON.stringify({ token: body.token, expiresAt: body.expiresAt }));
    contentPromise = null;
}

/** The report if it has already been loaded and the pass is still good, else null. */
export function peekContent() {
    return contentCache && hasPass() ? contentCache : null;
}

/** The whole report (see scripts/cfri/build_cfri_bundle.py for its shape). */
export function getContent() {
    if (!contentPromise) {
        contentPromise = fetchContent().then(
            (content) => {
                contentCache = content;
                return content;
            },
            (err) => {
                contentPromise = null;
                throw err;
            }
        );
    }
    return contentPromise;
}

async function fetchContent() {
    const pass = readPass();
    if (!pass) throw new CfriLockedError();
    const response = await fetch(`${properties.host}/cfri/content`, { headers: { [PASS_HEADER]: pass.token } });
    if (response.status === 401) {
        // Expired, or the password was changed since: ask for it again
        localStorage.removeItem(ftCfriPass);
        throw new CfriLockedError();
    }
    if (!response.ok) throw new Error("The report couldn't be loaded.");
    return response.json();
}

// Iframes and links can't send headers, so these carry the pass as ?token=
function withPass(path) {
    const pass = readPass();
    return `${properties.host}${path}?token=${encodeURIComponent(pass ? pass.token : "")}`;
}

export function mapUrl(id) {
    return withPass(`/cfri/maps/${encodeURIComponent(id)}`);
}

export function reportUrl() {
    return withPass("/cfri/report");
}
